import MuiTab from '@material-ui/core/Tab'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import React from 'react'

import { useAnalytics } from '../../lib/analytics'
import { Actions, Categories } from '../../lib/analytics/events'
import { Theme } from '../../lib/theming/Theme'

const useStyles = makeStyles<Theme>(theme => ({
  active: {
    color: theme.palette.secondary.main,
    opacity: '1',
  },
  button: {
    '&:hover': {
      color: theme.palette.common.black,
      opacity: '1',
    },
    alignItems: 'center',
    display: 'flex',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    height: '100%',
    justifyContent: 'center',
    letterSpacing: '0.055rem',
    minHeight: '2.9rem',
    minWidth: '100px',
    textDecoration: 'none',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.12rem',
      minWidth: '200px',
    },
    [theme.breakpoints.up('md')]: {
      minHeight: '3.5rem',
      minWidth: '160px',
    },
    textTransform: 'uppercase',
  },
  default: {
    color: theme.palette.text.primary,
    opacity: '0.5',
  },
}))

const useAssignmentStyles = makeStyles<Theme>(theme => ({
  active: {
    color: theme.palette.secondary.main,
    opacity: '1',
  },
  button: {
    '&:hover': {
      color: theme.palette.common.black,
      opacity: '1',
    },
    alignItems: 'center',
    display: 'flex',
    fontSize: theme.typography.button.fontSize,
    fontWeight: theme.typography.button.fontWeight,
    height: '100%',
    justifyContent: 'center',
    letterSpacing: '0rem',
    minWidth: '100px',
    padding: theme.spacing(2, 1),
    textDecoration: 'none',
    [theme.breakpoints.up('sm')]: {
      minWidth: '200px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '160px',
    },
    textTransform: 'uppercase',
  },
  default: {
    color: theme.palette.text.primary,
    opacity: '0.5',
  },
}))

export type TabProps = {
  active?: boolean
  index?: number
  label?: string | null
  onClick?: (i?: number, url?: string) => void
  page?: string
  target?: string | null
  url: string
  version?: string | null
}

const Tab = ({ active = false, index, url, label, page, target, version, onClick }: TabProps) => {
  const defaultClasses = useStyles()
  const AssignmentClasses = useAssignmentStyles()
  const { trackEvent } = useAnalytics()
  const router = useRouter()

  const classes = version === 'assignment' ? AssignmentClasses : defaultClasses

  const handleClick = async (e: React.MouseEvent) => {
    trackEvent({
      action: Actions.TabClick,
      category: Categories.Tickets,
      label,
      location: Categories.Tickets,
    })

    if (onClick) {
      e.preventDefault()
      onClick(index, url)
    } else if (page) {
      e.preventDefault()
      await router.push(page, url, { shallow: true })
    }
  }

  return (
    <MuiTab
      className={clsx(classes.button, {
        [classes.active]: active,
        [classes.default]: !active,
      })}
      component="a"
      href={url}
      label={label}
      tabIndex={0}
      target={target || undefined}
      onClick={handleClick}
    />
  )
}

export default Tab
